/// <amd-module name="Analytics/Common/RestClient/AnalyticsODataClient" />
/**
 * ---------------------------------------------------------
 * Hand-Authored File for OData Client behavior. Modelled after generated pattern.
 * ---------------------------------------------------------
 */

import * as Resources from "Analytics/Common/Resources";
import { IAnalyticsRestClient } from "Analytics/Common/RestClient/Analytics";
import { IVssPageContext, IVssRestClient, IVssRestClientOptions, RestClients, TeamFoundationHostType } from "VSS/Platform/Context";
import { RestClientBase, RestClientRequestParams } from "VSS/Platform/RestClientBase";

import {
    AnalyticsODataVersion,
    AnalyticsTransportOptions,
    BatchRequestMode,
    ODataCoreOptions,
    ODataQueryOptions
} from "Analytics/Common/ODataQueryOptions";

import { issueRequest, IVssRequestOptions } from "VSS/Platform/Fetch";

import { extractODataError } from "Utilities/AnalyticsExceptionUtilities";
import { generatePostPayload, getHostContext, sanitizeCollectionInPostPayload } from "Utilities/BatchRequestUtilities";
import { extractBatchPayloadResult } from "Utilities/BatchResponseUtilities";
import { generateGuid } from "Utilities/GuidGenerator";
import { checkODataPageValue, formatODataCommand, formatRequestUrl, generateCustomODataHeaders } from "Utilities/ODataClientUtilities";

export interface IAnalyticsODataClient extends IVssRestClient {
    query(command: string, queryOptions: ODataQueryOptions): Promise<any>;
    runNextLink(command: string, nextLinkUrl: string, queryOptions: ODataQueryOptions): Promise<any>;
}

class AnalyticsODataClient extends RestClientBase implements IAnalyticsODataClient {
    /** The maximum length of a Request (after the root) before the client switches to POST requests, when in Auto-mode.
     * This provides a safe margin below known browser issues.*/
    private static maxAutoGetURLLength = 1024;
    private static nextLinkKey = "@odata.nextLink";

    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    public async query(command: string, queryOptions: ODataQueryOptions): Promise<any> {
        let queryPromise;

        const getParams = this.generateGetRequestParams(command, queryOptions);

        if (await this.isBatchMode(command, queryOptions)) {
            const rootpath = await this.getRootPath();
            const getRequestUrl = formatRequestUrl(rootpath, getParams.routeTemplate, getParams.routeValues, getParams.queryParams);
            const postParams = this.generatePostRequestParams(command, queryOptions, getRequestUrl);
            queryPromise = this.runRequest(postParams, true, queryOptions.getBatchResponseInnerHeaders);
        } else {
            queryPromise = this.runRequest(getParams);
        }

        return queryOptions.followNextLink ? this.followNextLinks(command, queryPromise, queryOptions) : queryPromise;
    }

    /**
     * Bypasses routing logic to run explicit server-supplied Url for OData Query.
     * The only circumstance for using this is executing OData next-link page in response to user-interactions (e.g. virtualized lists).
     */
    public async runNextLink(command: string, nextLinkUrl: string, queryOptions: ODataQueryOptions): Promise<any> {
        let nextPagePromise;
        if (await this.isBatchMode(command, queryOptions)) {
            const postParams = this.generatePostRequestParams(command, queryOptions, nextLinkUrl);
            nextPagePromise = this.runRequest(postParams, true, queryOptions.getBatchResponseInnerHeaders);
        } else {
            const getParams = this.generateGetRequestParams(command, queryOptions);
            nextPagePromise = this._issueRequest(nextLinkUrl, queryOptions.oDataVersion, getParams);
        }
        return nextPagePromise;
    }

    /**
     * Evaluates if the specified Query needs to operate in batch mode.
     */
    private async isBatchMode(command: string, queryOptions: ODataQueryOptions): Promise<boolean> {
        let batchIsActive = false;
        if (queryOptions.batchRequestMode == null || queryOptions.batchRequestMode === BatchRequestMode.Disabled) {
            batchIsActive = false;
        } else if (queryOptions.batchRequestMode === BatchRequestMode.Enabled) {
            batchIsActive = true;
        } else {
            const getParams = this.generateGetRequestParams(command, queryOptions);
            // We need to obtain the Get URL in addition to POST URL, to put the GET URL in the POST payload.
            const rootpath = await this.getRootPath();
            const getRequestUrl = formatRequestUrl(rootpath, getParams.routeTemplate, getParams.routeValues, getParams.queryParams);

            batchIsActive =
                queryOptions.batchRequestMode === BatchRequestMode.AutoSelect && getRequestUrl.length > AnalyticsODataClient.maxAutoGetURLLength;
        }
        return batchIsActive;
    }

    /**
     * Recursively walks a series of OData Next links to populate a merged result.
     */
    private async followNextLinks(command: string, priorPromise: Promise<any>, queryOptions: ODataQueryOptions): Promise<any> {
        const priorPage = await priorPromise;
        if (priorPage && priorPage[AnalyticsODataClient.nextLinkKey]) {
            checkODataPageValue(priorPage);
            const currentLink = priorPage[AnalyticsODataClient.nextLinkKey];
            const currentPagePromise = this.runNextLink(command, currentLink, queryOptions);
            const currentPage = await this.followNextLinks(command, currentPagePromise, queryOptions);

            checkODataPageValue(priorPage);
            priorPage.value = priorPage.value.concat(currentPage.value);
            priorPage[AnalyticsODataClient.nextLinkKey] = undefined; // Remove next link since we pulled the data
        }
        return priorPage;
    }

    private async runRequest(
        requestParams: RestClientRequestParams,
        batchModeActive?: boolean,
        getBatchResponseInnerHeaders?: boolean
    ): Promise<any> {
        // The client handles exposure of human readable errors to insulate consumer from error structure differences based on verb.
        const result = await this.beginRequest(requestParams);
        try {
            return this.interpretRequestResult(result, batchModeActive, getBatchResponseInnerHeaders);
        } catch (error) {
            throw new Error(extractODataError(error));
        }
    }

    private async interpretRequestResult(result: any, batchModeActive?: boolean, getBatchResponseInnerHeaders?: boolean): Promise<any> {
        if (batchModeActive) {
            if (result != null && result.error) {
                // Note: On batch mode on/auto, an error is returned in body, not as an exception.
                throw new Error(extractODataError(result));
            } else {
                return extractBatchPayloadResult(result, getBatchResponseInnerHeaders);
            }
        } else {
            return result;
        }
    }

    private generateGetRequestParams(command: string, queryOptions: ODataQueryOptions): RestClientRequestParams {
        const apiVersion = queryOptions.oDataVersion;

        /* Reduce query options to Core Odata Params */
        const queryValues: ODataCoreOptions = {
            $apply: queryOptions.$apply,
            $count: queryOptions.$count,
            $expand: queryOptions.$expand,
            $filter: queryOptions.$filter,
            $format: queryOptions.$format,
            $orderby: queryOptions.$orderby,
            $search: queryOptions.$search,
            $select: queryOptions.$select,
            $skip: queryOptions.$skip,
            $top: queryOptions.$top
        };

        const routeTemplate = queryOptions.project
            ? `{project}/_odata/{apiVersion}/${queryOptions.entityType}`
            : `_odata/{apiVersion}/${queryOptions.entityType}`;
        const routeValues = queryOptions.project ? { apiVersion: apiVersion, project: queryOptions.project } : { apiVersion: apiVersion };

        const params: RestClientRequestParams = {
            apiVersion: apiVersion,
            queryParams: queryValues,
            method: "GET",
            routeTemplate: routeTemplate,
            routeValues: routeValues,
            customHeaders: generateCustomODataHeaders(queryOptions),
            command: formatODataCommand(command)
        };

        return params;
    }

    /**
     * Generates neccessary request parameters for Post Request
     * @param queryOptions
     * @param sanitizedGetQueryUrl - Used to populate body of request
     */
    private generatePostRequestParams(command: string, queryOptions: ODataQueryOptions, getQueryUrl: string): RestClientRequestParams {
        const apiVersion = queryOptions.oDataVersion;
        const batchIdentifier = generateGuid();
        const sanitizedGetQueryUrl = sanitizeCollectionInPostPayload(getQueryUrl, getHostContext(queryOptions.pageContext));
        const routeTemplate = queryOptions.project ? "{project}/_odata/{apiVersion}/$batch" : "_odata/{apiVersion}/$batch";
        const routeValues = queryOptions.project ? { apiVersion: apiVersion, project: queryOptions.project } : { apiVersion: apiVersion };
        const params: RestClientRequestParams = {
            apiVersion: apiVersion,
            method: "POST",
            routeTemplate: routeTemplate,
            routeValues: routeValues,
            isRawData: true,
            customHeaders: generateCustomODataHeaders(queryOptions, batchIdentifier),
            body: generatePostPayload(sanitizedGetQueryUrl, batchIdentifier, queryOptions),
            returnRawResponse: true,
            command: formatODataCommand(command)
        };
        return params;
    }
}

RestClients.add("IAnalyticsODataClient", {
    factory: AnalyticsODataClient,
    options: {
        serviceInstanceType: "0000003c-0000-8888-8000-000000000000"
    }
});
